import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
// userAccess, not commonFunction: importing commonFunction first creates an
// import cycle with utility/index that crashes the app on start.
import { canSeeNotifications } from "../utility/userAccess";
import { useEffect, Suspense, lazy } from "react";
import PublicAuth from "./publicAuth";
import RequireAuth from "./RequireAuth";
import MainSkeleton from "../component/skeleton/headerFooter";
import Login from "../pages/auth/login.js";
import ErrorBoundary from "../component/ErrorBoundary";

const MainLayout = lazy(() => import("../component/layout/MainLayout"));

// ── Lazy loaded pages ─────────────────────────────────────────────────────────
const Dashboard = lazy(() => import("../pages/dashboard/index.js"));
const Users = lazy(() => import("../pages/userAccount/users"));
const PropertyOwner = lazy(() => import("../pages/userAccount/propertyOwner"));
const Brands = lazy(() => import("../pages/productRelated/brands"));
const Recipes = lazy(() => import("../pages/productRelated/recipes"));
const Warehouse = lazy(() => import("../pages/warehouseRelated/warehouses"));
const Credit = lazy(() => import("../pages/reports/credit"));
const WarehouseStock = lazy(() =>
  import("../pages/warehouseRelated/warehouseStocks"),
);
const StoreStocks = lazy(() => import("../pages/storeRelated/storeStocks"));
const StoreGRN = lazy(() => import("../pages/storeRelated/storeGRN"));
const GRN = lazy(() => import("../pages/storeRelated/GRN"));
const WarehouseToStore = lazy(() =>
  import("../pages/storeTransfers/warehouseToStore"),
);
const WarehouseToWarehouse = lazy(() =>
  import("../pages/storeTransfers/warehouseToWarehouse"),
);
const StoreToStore = lazy(() => import("../pages/storeTransfers/storeToStore"));
const StoreToWarehouse = lazy(() =>
  import("../pages/storeTransfers/storeToWarehouse"),
);
const WarehouseGRN = lazy(() =>
  import("../pages/warehouseRelated/warehouseGRN"),
);
const SKUTimeline = lazy(() => import("../pages/storeRelated/SKUTimeline"));
const SKUTimelineWarehouse = lazy(() =>
  import("../pages/warehouseRelated/SKUTimeline"),
);
const RTVStocks = lazy(() => import("../pages/storeRelated/rtvStocks"));
const RTVStocksWarehouse = lazy(() =>
  import("../pages/warehouseRelated/rtvStocks"),
);
const AppVersion = lazy(() => import("../pages/superAdmin/appVersion"));
const Announcements = lazy(() =>
  import("../pages/superAdmin/banner/announcement.js"),
);
const Orders = lazy(() => import("../pages/invoiceAndBilling/order"));
const ReturnOrder = lazy(() => import("../pages/invoiceAndBilling/return"));
const PurcheseOrder = lazy(() => import("../pages/storeRelated/po"));
const DiscordStoreStock = lazy(() =>
  import("../pages/storeRelated/discordStoreStock"),
);
const Banner = lazy(() => import("../pages/superAdmin/banner"));
const OnlineOrder = lazy(() => import("../pages/superAdmin/onlineOrder"));
const WarehousePo = lazy(() => import("../pages/warehouseRelated/warehousePo"));
const WarehouseReport = lazy(() =>
  import("../pages/storeRelated/pendingAndCashoutReport/warehouseReports"),
);
const StoreReport = lazy(() =>
  import("../pages/storeRelated/pendingAndCashoutReport/storeReports"),
);
const DeliveryDetailPage = lazy(() =>
  import("../pages/superAdmin/onlineOrder/deliveryDetail"),
);
const EditCostAndSellingPrice = lazy(() =>
  import("../pages/storeRelated/editCostSellingPrice"),
);
const StockLedger = lazy(() => import("../pages/storeRelated/stockLedger"));
const DiscordWarehouseStocks = lazy(() =>
  import("../pages/warehouseRelated/discordWarehouseStock"),
);
const Store = lazy(() => import("../pages/storeRelated/store"));
const StoreRoyalty = lazy(() =>
  import("../pages/storeRelated/store/storeDetail"),
);
const Products = lazy(() => import("../pages/productRelated/products"));
const Category = lazy(() => import("../pages/productRelated/productCategory"));
const BrandOwner = lazy(() => import("../pages/userAccount/brandOwner.js"));
const Customer = lazy(() => import("../pages/userAccount/customer"));
const RoyaltyHistory = lazy(() =>
  import("../pages/storeRelated/store/royaltyHistory.js"),
);
const Profile = lazy(() => import("../pages/profile/index.js"));
const AddPo = lazy(() => import("../pages/storeRelated/po/addPo.js"));
const AddStoreStock = lazy(() =>
  import("../pages/storeRelated/storeStocks/addstoreStocks"),
);
const CreateInvoice = lazy(() =>
  import("../pages/storeRelated/po/createInvoice.js"),
);
const StnS2S = lazy(() =>
  import("../pages/storeTransfers/storeToStore/makeTransfer.js"),
);
const Invoice = lazy(() => import("../pages/storeRelated/po/invoices.js"));
const Payment = lazy(() => import("../pages/storeRelated/po/payment.js"));
const B2BProducts = lazy(() => import("../pages/eProducts/b2b-products"));
const VendorProducts = lazy(() => import("../pages/eProducts/vendor-products"));
const B2BBrands = lazy(() => import("../pages/eProducts/b2bBrands"));
const B2BPO = lazy(() => import("../pages/storeRelated/po/b2bPo.js"));
const B2BDashboard = lazy(() =>
  import("../pages/storeRelated/po/b2bDashboard.js"),
);
const StoreMap = lazy(() => import("../pages/storeRelated/storeMap"));
const Advertisements = lazy(() => import("../pages/superAdmin/advertisements"));
const AdPurchases = lazy(() =>
  import("../pages/superAdmin/advertisements/purchases"),
);
const Coupons = lazy(() => import("../pages/marketing/coupon"));
const AddCoupon = lazy(() => import("../pages/marketing/coupon/addCoupon"));
const ViewCoupon = lazy(() => import("../pages/marketing/coupon/viewCoupon"));
const ManualEinvoices = lazy(() => import("../pages/invoiceAndBilling/eInvoice"));
const CreateManualEinvoice = lazy(() =>
  import("../pages/invoiceAndBilling/eInvoice/createEinvoice"),
);
const VendorStoreMapping = lazy(() =>
  import("../pages/superAdmin/vendorStoreMapping"),
);
const B2BWalletReport = lazy(() => import("../pages/reports/b2bWallet"));
const CashHandover = lazy(() => import("../pages/reports/cashHandover"));
const B2BCashback = lazy(() => import("../pages/superAdmin/b2bCashback"));
const Tasks = lazy(() => import("../pages/tasks"));
const TaskReview = lazy(() => import("../pages/tasks/reviewQueue"));
const TaskTemplates = lazy(() => import("../pages/tasks/templates"));
const TaskAnalytics = lazy(() => import("../pages/tasks/analytics"));
const TaskRecurring = lazy(() => import("../pages/tasks/recurringTemplates"));
const Notifications = lazy(() => import("../pages/notifications"));
const AiAssistant = lazy(() => import("../pages/aiAssistant"));
const AiGrn = lazy(() => import("../pages/aiGrn"));
const AiGrnApprovalRequests = lazy(() => import("../pages/aiGrn/ApprovalRequestsPage"));

function Router() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<MainSkeleton />}>
        <BrowserRouter>
          <Routes>
            <Route
              path="/"
              element={
                <PublicAuth>
                  <Login />
                </PublicAuth>
              }
            />
            <Route
              path="/detail-page-for-delivery/:id"
              element={<DeliveryDetailPage />}
            />
            <Route element={<MainLayout />}>
              <Route
                path="/dashboard"
                element={
                  <RequireAuth>
                    <Dashboard />
                  </RequireAuth>
                }
              />
              <Route
                path="/users"
                element={
                  <RequireAuth>
                    <Users />
                  </RequireAuth>
                }
              />
              <Route
                path="/property-owner"
                element={
                  <RequireAuth>
                    <PropertyOwner />
                  </RequireAuth>
                }
              />
              <Route
                path="/brands"
                element={
                  <RequireAuth>
                    <Brands />
                  </RequireAuth>
                }
              />
              <Route
                path="/store"
                element={
                  <RequireAuth>
                    <Store />
                  </RequireAuth>
                }
              />
              <Route
                path="/store-royalty"
                element={
                  <RequireAuth>
                    <StoreRoyalty />
                  </RequireAuth>
                }
              />
              <Route
                path="/payment-royalty"
                element={
                  <RequireAuth>
                    <StoreRoyalty />
                  </RequireAuth>
                }
              />
              <Route
                path="/royalty-history"
                element={
                  <RequireAuth>
                    <RoyaltyHistory />
                  </RequireAuth>
                }
              />
              <Route
                path="/warehouse"
                element={
                  <RequireAuth>
                    <Warehouse />
                  </RequireAuth>
                }
              />
              <Route
                path="/products"
                element={
                  <RequireAuth>
                    <Products />
                  </RequireAuth>
                }
              />
              <Route
                path="/b2b-products"
                element={
                  <RequireAuth>
                    <B2BProducts />
                  </RequireAuth>
                }
              />
              <Route
                path="/vendor-products"
                element={
                  <RequireAuth>
                    <VendorProducts />
                  </RequireAuth>
                }
              />
              <Route
                path="/b2b-brands"
                element={
                  <RequireAuth>
                    <B2BBrands />
                  </RequireAuth>
                }
              />
              <Route
                path="/category"
                element={
                  <RequireAuth>
                    <Category />
                  </RequireAuth>
                }
              />
              <Route
                path="/customer"
                element={
                  <RequireAuth>
                    <Customer />
                  </RequireAuth>
                }
              />
              <Route
                path="/my-profile"
                element={
                  <RequireAuth>
                    <Profile />
                  </RequireAuth>
                }
              />
              <Route
                path="/brand-owner"
                element={
                  <RequireAuth>
                    <BrandOwner />
                  </RequireAuth>
                }
              />
              {/* <Route
              path="/warehouse-stock"
              element={
                <RequireAuth>
                  <WarehouseStock />
                </RequireAuth>
              }
            /> */}
              <Route
                path="/grn"
                element={
                  <RequireAuth>
                    <GRN />
                  </RequireAuth>
                }
              />
              <Route
                path="/add-grn"
                element={
                  <RequireAuth>
                    <AddStoreStock />
                  </RequireAuth>
                }
              />
              <Route
                path="/store-grn"
                element={
                  <RequireAuth>
                    <StoreGRN />
                  </RequireAuth>
                }
              />
              {/* <Route
              path="/warehouse-grn"
              element={
                <RequireAuth>
                  <WarehouseGRN />
                </RequireAuth>
              }
            /> */}
              {/* <Route
              path="/warehouse-to-store"
              element={
                <RequireAuth>
                  <WarehouseToStore />
                </RequireAuth>
              }
            /> */}
              {/* <Route
              path="/warehouse-to-warehouse"
              element={
                <RequireAuth>
                  <WarehouseToWarehouse />
                </RequireAuth>
              }
            /> */}
              {/* <Route
              path="/store-to-warehouse"
              element={
                <RequireAuth>
                  <StoreToWarehouse />
                </RequireAuth>
              }
            /> */}
              <Route
                path="/store-to-store"
                element={
                  <RequireAuth>
                    <StoreToStore />
                  </RequireAuth>
                }
              />
              <Route
                path="/stn-s2s"
                element={
                  <RequireAuth>
                    <StnS2S />
                  </RequireAuth>
                }
              />
              <Route
                path="/SKU-Timeline"
                element={
                  <RequireAuth>
                    <SKUTimeline />
                  </RequireAuth>
                }
              />
              <Route
                path="/SKU-Timeline-warehouse"
                element={
                  <RequireAuth>
                    <SKUTimelineWarehouse />
                  </RequireAuth>
                }
              />
              <Route
                path="/rtvstocks"
                element={
                  <RequireAuth>
                    <RTVStocks />
                  </RequireAuth>
                }
              />
              <Route
                path="/warehouse-rtvstocks"
                element={
                  <RequireAuth>
                    <RTVStocksWarehouse />
                  </RequireAuth>
                }
              />
              <Route
                path="/discard-warehouse-stocks"
                element={
                  <RequireAuth>
                    <DiscordWarehouseStocks />
                  </RequireAuth>
                }
              />
              <Route
                path="/discard-store-stocks"
                element={
                  <RequireAuth>
                    <DiscordStoreStock />
                  </RequireAuth>
                }
              />
              <Route
                path="/order"
                element={
                  <RequireAuth>
                    <Orders />
                  </RequireAuth>
                }
              />
              <Route
                path="/order-return"
                element={
                  <RequireAuth>
                    <ReturnOrder />
                  </RequireAuth>
                }
              />
              <Route
                path="/store-po"
                element={
                  <RequireAuth>
                    <PurcheseOrder />
                  </RequireAuth>
                }
              />
              <Route
                path="/b2b-po"
                element={
                  <RequireAuth>
                    <B2BPO />
                  </RequireAuth>
                }
              />
              <Route
                path="/b2b-dashboard"
                element={
                  <RequireAuth>
                    <B2BDashboard />
                  </RequireAuth>
                }
              />
              <Route
                path="/create-invoice-b2b"
                element={
                  <RequireAuth>
                    <CreateInvoice />
                  </RequireAuth>
                }
              />
              <Route
                path="/payment"
                element={
                  <RequireAuth>
                    <Payment />
                  </RequireAuth>
                }
              />
              <Route
                path="/invoice-b2b"
                element={
                  <RequireAuth>
                    <Invoice />
                  </RequireAuth>
                }
              />
              <Route
                path="/add-po"
                element={
                  <RequireAuth>
                    <AddPo />
                  </RequireAuth>
                }
              />
              <Route
                path="/warehouse-po"
                element={
                  <RequireAuth>
                    <WarehousePo />
                  </RequireAuth>
                }
              />
              <Route
                path="/app-version"
                element={
                  <RequireAuth>
                    <AppVersion />
                  </RequireAuth>
                }
              />
              <Route
                path="/announcements"
                element={
                  <RequireAuth>
                    <Announcements />
                  </RequireAuth>
                }
              />
              <Route
                path="/banner"
                element={
                  <RequireAuth>
                    <Banner />
                  </RequireAuth>
                }
              />
              <Route
                path="/advertisements"
                element={
                  <RequireAuth>
                    <Advertisements />
                  </RequireAuth>
                }
              />
              <Route
                path="/ad-purchases"
                element={
                  <RequireAuth>
                    <AdPurchases />
                  </RequireAuth>
                }
              />
              <Route
                path="/online-order"
                element={
                  <RequireAuth>
                    <OnlineOrder />
                  </RequireAuth>
                }
              />
              <Route
                path="/cash-out-report"
                element={
                  <RequireAuth>
                    <StoreReport />
                  </RequireAuth>
                }
              />
              <Route
                path="/cash-in-report"
                element={
                  <RequireAuth>
                    <StoreReport />
                  </RequireAuth>
                }
              />
              <Route
                path="/gstr"
                element={
                  <RequireAuth>
                    <StoreReport />
                  </RequireAuth>
                }
              />
              <Route
                path="/credit"
                element={
                  <RequireAuth>
                    <Credit />
                  </RequireAuth>
                }
              />
              <Route
                path="/rtv-report"
                element={
                  <RequireAuth>
                    <RTVStocks />
                  </RequireAuth>
                }
              />
              <Route
                path="/discard-report"
                element={
                  <RequireAuth>
                    <DiscordStoreStock />
                  </RequireAuth>
                }
              />
              <Route
                path="/billing-report"
                element={
                  <RequireAuth>
                    <Orders />
                  </RequireAuth>
                }
              />
              <Route
                path="/pending-cash-report"
                element={
                  <RequireAuth>
                    <StoreReport />
                  </RequireAuth>
                }
              />
              <Route
                path="/stock-ledger"
                element={
                  <RequireAuth>
                    <StoreReport />
                  </RequireAuth>
                }
              />
              <Route
                path="/current-stock-report"
                element={
                  <RequireAuth>
                    <StoreStocks />
                  </RequireAuth>
                }
              />
              <Route
                path="/grn-report"
                element={
                  <RequireAuth>
                    <StoreReport />
                  </RequireAuth>
                }
              />
              <Route
                path="/shift-report"
                element={
                  <RequireAuth>
                    <StoreReport />
                  </RequireAuth>
                }
              />
              <Route
                path="/return-report"
                element={
                  <RequireAuth>
                    <ReturnOrder />
                  </RequireAuth>
                }
              />
              <Route
                path="/warehouse-grn-report"
                element={
                  <RequireAuth>
                    <WarehouseReport />
                  </RequireAuth>
                }
              />
              <Route
                path="/recipes"
                element={
                  <RequireAuth>
                    <Recipes />
                  </RequireAuth>
                }
              />
              <Route
                path="/store-map"
                element={
                  <RequireAuth>
                    <StoreMap />
                  </RequireAuth>
                }
              />
              <Route
                path="/edit-cost-selling-price"
                element={<EditCostAndSellingPrice />}
              />
              <Route
                path="/e-invoices"
                element={
                  <RequireAuth>
                    <ManualEinvoices />
                  </RequireAuth>
                }
              />
              <Route
                path="/e-invoices/new"
                element={
                  <RequireAuth>
                    <CreateManualEinvoice />
                  </RequireAuth>
                }
              />
              <Route
                path="/e-invoices/edit/:id"
                element={
                  <RequireAuth>
                    <CreateManualEinvoice />
                  </RequireAuth>
                }
              />
              <Route
                path="/coupons"
                element={
                  <RequireAuth>
                    <Coupons />
                  </RequireAuth>
                }
              />
              <Route
                path="/coupons/add"
                element={
                  <RequireAuth>
                    <AddCoupon />
                  </RequireAuth>
                }
              />
              <Route
                path="/coupons/edit/:id"
                element={
                  <RequireAuth>
                    <AddCoupon />
                  </RequireAuth>
                }
              />
              <Route
                path="/coupons/detail/:id"
                element={
                  <RequireAuth>
                    <ViewCoupon />
                  </RequireAuth>
                }
              />
              <Route
                path="/vendor-store-mapping"
                element={
                  <RequireAuth>
                    <VendorStoreMapping />
                  </RequireAuth>
                }
              />
              <Route
                path="/b2b-wallet-report"
                element={
                  <RequireAuth>
                    <B2BWalletReport />
                  </RequireAuth>
                }
              />
              <Route
                path="/cash-handover"
                element={
                  <RequireAuth>
                    <CashHandover />
                  </RequireAuth>
                }
              />
              <Route
                path="/b2b-cashback"
                element={
                  <RequireAuth>
                    <B2BCashback />
                  </RequireAuth>
                }
              />
              <Route
                path="/tasks"
                element={
                  <RequireAuth>
                    <Tasks />
                  </RequireAuth>
                }
              />
              <Route
                path="/tasks/review"
                element={
                  <RequireAuth>
                    <TaskReview />
                  </RequireAuth>
                }
              />
              <Route
                path="/tasks/templates"
                element={
                  <RequireAuth>
                    <TaskTemplates />
                  </RequireAuth>
                }
              />
              <Route
                path="/tasks/analytics"
                element={
                  <RequireAuth>
                    <TaskAnalytics />
                  </RequireAuth>
                }
              />
              <Route
                path="/tasks/recurring"
                element={
                  <RequireAuth>
                    <TaskRecurring />
                  </RequireAuth>
                }
              />
              <Route
                path="/notifications"
                element={
                  <RequireAuth>
                    {/* Super Admin / Biller have no notifications */}
                    {canSeeNotifications() ? (
                      <Notifications />
                    ) : (
                      // each back to their own home page (as after login)
                      <Navigate
                        to={localStorage.getItem("userType") === "B" ? "/store-royalty" : "/dashboard"}
                        replace
                      />
                    )}
                  </RequireAuth>
                }
              />
              <Route
                path="/ai-assistant"
                element={
                  <RequireAuth>
                    <AiAssistant />
                  </RequireAuth>
                }
              />
              <Route
                path="/ai-grn"
                element={
                  <RequireAuth>
                    <AiGrn />
                  </RequireAuth>
                }
              />
              <Route
                path="/ai-grn/approval-requests"
                element={
                  <RequireAuth>
                    <AiGrnApprovalRequests />
                  </RequireAuth>
                }
              />
              <Route path="*" element={<h1>Not found</h1>} />
            </Route>
          </Routes>
        </BrowserRouter>
      </Suspense>
    </ErrorBoundary>
  );
}

export default Router;
